'use client';

import { useEffect, useState } from 'react';

import { FormSubmissionModal } from '@/components/forms/FormSubmissionModal/FormSubmissionModal';
import PackageCardsSection from '@/components/sections/PackageCardsSection';
import { BookingWidgetClient } from '@/features/booking/components/BookingWidgetClient';
import { useBookingFlowStore } from '@/features/booking/store/useBookingFlowStore';
import { mapDoctorToBookingDoctor } from '@/features/booking/utils/mapDoctorToBookingDoctor';
import {
  gpAccessEnquiryDefaultValues,
  gpAccessEnquiryFields,
  GPAccessEnquirySchema,
} from '@/features/gpAccess/forms/GPAccessEnquiry';
import { getDoctorsListWithBookingFields } from '@/features/specialists/data/doctorsData';
import type { Doctor } from '@/types/doctor';

import type { GPPackageCard, GPPackagesSectionData } from './types';

interface Props {
  data: GPPackagesSectionData;
}

const GPPackagesSection = ({ data }: Props) => {
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState<GPPackageCard | null>(
    null,
  );
  const [bookingWidgetOpen, setBookingWidgetOpen] = useState(false);

  const setSelectedDoctor = useBookingFlowStore((s) => s.setSelectedDoctor);
  const setCurrentStep = useBookingFlowStore((s) => s.setCurrentStep);
  const setNotes = useBookingFlowStore((s) => s.setNotes);
  const setDepositFee = useBookingFlowStore((s) => s.setDepositFee);
  const setPackagePrice = useBookingFlowStore((s) => s.setPackagePrice);
  const setShowModal = useBookingFlowStore((s) => s.setShowModal);
  const showModal = useBookingFlowStore((s) => s.showModal);

  useEffect(() => {
    if (!showModal && bookingWidgetOpen) {
      setBookingWidgetOpen(false);
    }
  }, [showModal, bookingWidgetOpen]);

  const handleEnquire = (card: GPPackageCard): void => {
    setSelectedPackage(card);
    setHasBeenOpened(true);
    setEnquiryOpen(true);
  };

  const handleBook = async (card: GPPackageCard): Promise<void> => {
    const apiId = card.apiIds?.[0];
    if (!apiId) {
      handleEnquire(card);
      return;
    }

    const doctors = await getDoctorsListWithBookingFields();
    const doctor = doctors.find((d) => d.apiId === apiId);
    if (!doctor) {
      console.warn(
        `GPPackagesSection: no bookable doctor found for apiId "${apiId}" (package: "${card.title}"). Falling back to enquiry.`,
      );
      handleEnquire(card);
      return;
    }

    const bookingDoctor = mapDoctorToBookingDoctor(doctor as Doctor);
    setSelectedDoctor(bookingDoctor);
    setCurrentStep(1);
    setNotes(card.title);
    if (card.depositFee !== undefined) {
      setDepositFee(card.depositFee);
    }
    if (card.price !== null) {
      setPackagePrice(Number(card.price));
    }
    setShowModal(true);
    setBookingWidgetOpen(true);
  };

  // The GP card contract historically named the top informational text
  // `disclaimer`. Keep rendering it in the same place through `text4`.
  const cards: GPPackageCard[] = data.cards.map((card) => ({
    ...card,
    text4: card.text4 ?? card.disclaimer,
    disclaimer: undefined,
  }));
  // Preserve the existing rendered casing while the shared card now honours
  // the configured `enquireLabel`.
  const labels = data.labels?.enquireLabel
    ? {
        ...data.labels,
        enquireLabel: {
          ...data.labels.enquireLabel,
          content: 'Enquire Now',
        },
      }
    : data.labels;

  return (
    <>
      <PackageCardsSection
        cards={cards}
        labels={labels}
        footerCta={{
          title: 'Book an appointment',
          functionality: 'book_appointment',
        }}
        canBook={(card) => Boolean(card.apiIds?.length)}
        onEnquire={handleEnquire}
        onBook={handleBook}
      />

      {hasBeenOpened && (
        <FormSubmissionModal
          open={enquiryOpen}
          onOpenChange={setEnquiryOpen}
          formId="GPAccessEnquiry"
          schema={GPAccessEnquirySchema}
          fields={gpAccessEnquiryFields}
          defaultValues={gpAccessEnquiryDefaultValues}
          title={
            selectedPackage?.title
              ? `Enquire about ${selectedPackage.title}`
              : 'Enquire about Private GP Packages'
          }
          metadata={{
            serviceName: 'Private GP',
            pageType: 'service',
            source: 'private-gp-packages',
            packageName: selectedPackage?.title,
            packagePrice: selectedPackage?.price,
            packageType: selectedPackage?.testType,
          }}
        />
      )}
      {bookingWidgetOpen && <BookingWidgetClient />}
    </>
  );
};

export default GPPackagesSection;
